import React from 'react';
import { Thermometer, Droplets, Zap, Sun, Users } from 'lucide-react';

export interface SensorDisplayStyle02Props {
  type: 'temperature' | 'humidity' | 'power' | 'lux' | 'occupancy';
  value?: number | boolean;
  unit?: string;
  label?: string;
}

const SENSOR_CONFIG: Record<string, {
  icon: React.ElementType;
  color: string;
  bgColor: string;
  unit: string;
  label: string;
  formatValue: (v: any) => string;
}> = {
  temperature: {
    icon: Thermometer,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    unit: '°C',
    label: '溫度',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  humidity: {
    icon: Droplets,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    unit: '%',
    label: '濕度',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  power: {
    icon: Zap,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10',
    unit: 'W',
    label: '功率',
    formatValue: (v) => {
      if (typeof v !== 'number') return '--';
      return v >= 1000 ? `${(v / 1000).toFixed(2)} kW` : `${v.toFixed(0)} W`;
    }
  },
  voltage: {
    icon: Zap,
    color: 'text-yellow-300',
    bgColor: 'bg-yellow-500/10',
    unit: 'V',
    label: '電壓',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  current: {
    icon: Zap,
    color: 'text-orange-300',
    bgColor: 'bg-orange-500/10',
    unit: 'A',
    label: '電流',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  energy: {
    icon: Zap,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    unit: 'kWh',
    label: '累積用電',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  lux: {
    icon: Sun,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    unit: 'lux',
    label: '光照',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  occupancy: {
    icon: Users,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    unit: '',
    label: '狀態',
    formatValue: (v) => v ? '有人' : '無人'
  }
};

/**
 * SensorDisplay Style 02 - Inline
 * Horizontal inline layout with icon, label, and value
 */
export const Style02_Inline: React.FC<SensorDisplayStyle02Props> = ({
  type,
  value,
  unit,
  label
}) => {
  const config = SENSOR_CONFIG[type];
  if (!config) return null;

  const displayUnit = unit ?? config.unit;
  const displayLabel = label ?? config.label;
  const displayValue = config.formatValue(value);
  const Icon = config.icon;

  const valueWithUnit = type === 'power' ? displayValue : `${displayValue}${displayUnit}`;

  return (
    <div className={`flex items-center justify-between px-4 py-3 rounded-xl ${config.bgColor}`}>
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${config.color}`} />
        <span className="text-sm text-slate-300">{displayLabel}</span>
      </div>
      <span className={`text-lg font-bold ${config.color}`}>
        {valueWithUnit}
      </span>
    </div>
  );
};

export default Style02_Inline;
